import { supabase } from '@/lib/supabase/client';
import type { Location, LocationInsert, LocationUpdate } from '@/types/location';
import { locationLaunchesService } from './location-launches';
import { customerAccountsService as customerAccountsServiceImpl } from './customer-accounts';

const STORE_NAME_MIGRATION_ERROR =
  'locations.store_name is not available in this environment yet. Run migration 055_add_locations_store_name_mapping.sql and try again.';
const LOCATION_LIFECYCLE_MIGRATION_ERROR =
  'Location archive fields are not available in this environment yet. Run migration 059_add_location_lifecycle_archive_fields.sql and try again.';
const CUSTOMER_OPS_CAMPAIGN_MIGRATION_ERROR =
  'Improvement campaign fields are not available in this environment yet. Run migration 060_customer_ops_phase1.sql and try again.';

// Helper to get current user ID
async function getCurrentUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user?.id ?? null;
}

export const locationsService = {
  async getAll(): Promise<Location[]> {
    try {
      const { data, error } = await supabase.from('locations').select('*').order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching locations:', error);
        return [];
      }

      return (data as Location[]) || [];
    } catch (error) {
      console.error('Error fetching locations:', error);
      return [];
    }
  },

  async getById(id: string): Promise<Location | null> {
    try {
      const { data, error } = await supabase.from('locations').select('*').eq('id', id).single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned
          return null;
        }
        console.error('Error fetching location:', error);
        return null;
      }

      return (data as Location) || null;
    } catch (error) {
      console.error('Error fetching location:', error);
      return null;
    }
  },

  async create(data: LocationInsert): Promise<Location> {
    try {
      const userId = await getCurrentUserId();

      const insertData: Record<string, unknown> = {
        location_number: data.location_number,
        location_name: data.location_name,
        status: data.status ?? 'pre_contract_signed',
        deal_status: data.deal_status ?? 'paid_contract_pending',
        equipment_status: data.equipment_status ?? 'not_yet_ordered',
        installation_status: data.installation_status ?? 'installation_pending',
        onboarding_status: data.onboarding_status ?? 'onboarding_info_sent',
        address: data.address ?? null,
        phone: data.phone ?? null,
        logo_url: data.logo_url ?? null,
        contract_signed_date: data.contract_signed_date ?? null,
        contract_renewal_date: data.contract_renewal_date ?? null,
        contract_term_months: data.contract_term_months ?? null,
        contract_signee_name: data.contract_signee_name ?? null,
        contract_signee_title: data.contract_signee_title ?? null,
        contract_file_path: data.contract_file_path ?? null,
        contract_file_url: data.contract_file_url ?? null,
        installation_date: data.installation_date ?? null,
        launch_date: data.launch_date ?? null,
        monthly_license_fee: data.monthly_license_fee ?? null,
        license_fee_period: data.license_fee_period ?? 'monthly',
        pos_system: data.pos_system ?? null,
        excluded_from_upcoming_launches: false, // Default to false for new locations
        created_by: userId,
        updated_by: userId,
      };

      if (data.store_name !== undefined) {
        const trimmedStoreName = data.store_name?.trim();
        insertData.store_name = trimmedStoreName ? trimmedStoreName : null;
      }

      if (data.lifecycle_status !== undefined) insertData.lifecycle_status = data.lifecycle_status;
      if (data.archive_reason_code !== undefined) insertData.archive_reason_code = data.archive_reason_code;
      if (data.archive_reason_detail !== undefined) insertData.archive_reason_detail = data.archive_reason_detail;
      if (data.contract_end_date !== undefined) insertData.contract_end_date = data.contract_end_date;
      if (data.contract_end_reason !== undefined) insertData.contract_end_reason = data.contract_end_reason;
      if (data.ended_by !== undefined) insertData.ended_by = data.ended_by;
      if (data.archived_at !== undefined) insertData.archived_at = data.archived_at;
      if (data.archived_by !== undefined) insertData.archived_by = data.archived_by;
      
      // Only include installation_type if provided (column may not exist if migration hasn't been run)
      if (data.installation_type !== undefined) {
        insertData.installation_type = data.installation_type;
      }

      const { data: newLocation, error } = await supabase.from('locations').insert(insertData).select().single();

      if (error) {
        console.error('Error creating location:', error);
        if (error.message?.includes('store_name') && insertData.store_name !== undefined) {
          throw new Error(STORE_NAME_MIGRATION_ERROR);
        }
        throw new Error(`Failed to create location: ${error.message}`);
      }

      if (newLocation) {
        await locationLaunchesService.ensurePrimaryForLocation({
          locationId: newLocation.id,
          status: newLocation.installation_status,
          installationDate: newLocation.installation_date,
          launchDate: newLocation.launch_date,
          installationType: newLocation.installation_type ?? null,
          excludedFromUpcoming: newLocation.excluded_from_upcoming_launches,
          notes: 'Primary launch created with location',
        });

        try {
          await customerAccountsServiceImpl.ensureSingleStoreAccountForLocation(newLocation as Location);
        } catch (accountError) {
          console.warn('Customer account auto-link skipped:', accountError);
        }
      }

      return newLocation as Location;
    } catch (error) {
      console.error('Error creating location:', error);
      throw error;
    }
  },

  async update(id: string, data: LocationUpdate): Promise<Location> {
    try {
      const userId = await getCurrentUserId();

      const updateData: Record<string, unknown> = {
        updated_by: userId,
      };
      
      // Only include fields that are provided
      if (data.location_number !== undefined) updateData.location_number = data.location_number;
      if (data.store_name !== undefined) {
        const trimmedStoreName = data.store_name?.trim();
        updateData.store_name = trimmedStoreName ? trimmedStoreName : null;
      }
      
      // Include installation_type if provided (column may not exist if migration hasn't been run)
      if (data.installation_type !== undefined && data.installation_type !== null) {
        updateData.installation_type = data.installation_type;
      }
      if (data.location_name !== undefined) updateData.location_name = data.location_name;
      if (data.status !== undefined) updateData.status = data.status;
      if (data.deal_status !== undefined) updateData.deal_status = data.deal_status;
      if (data.equipment_status !== undefined) updateData.equipment_status = data.equipment_status;
      if (data.installation_status !== undefined) updateData.installation_status = data.installation_status;
      if (data.onboarding_status !== undefined) updateData.onboarding_status = data.onboarding_status;
      if (data.address !== undefined) updateData.address = data.address;
      if (data.phone !== undefined) updateData.phone = data.phone;
      if (data.logo_url !== undefined) updateData.logo_url = data.logo_url;
      if (data.contract_signed_date !== undefined) updateData.contract_signed_date = data.contract_signed_date;
      if (data.contract_renewal_date !== undefined) updateData.contract_renewal_date = data.contract_renewal_date;
      if (data.contract_term_months !== undefined) updateData.contract_term_months = data.contract_term_months;
      if (data.contract_signee_name !== undefined) updateData.contract_signee_name = data.contract_signee_name;
      if (data.contract_signee_title !== undefined) updateData.contract_signee_title = data.contract_signee_title;
      if (data.contract_file_path !== undefined) updateData.contract_file_path = data.contract_file_path;
      if (data.contract_file_url !== undefined) updateData.contract_file_url = data.contract_file_url;
      if (data.installation_date !== undefined) updateData.installation_date = data.installation_date;
      if (data.launch_date !== undefined) updateData.launch_date = data.launch_date;
      if (data.monthly_license_fee !== undefined) updateData.monthly_license_fee = data.monthly_license_fee;
      if (data.license_fee_period !== undefined) updateData.license_fee_period = data.license_fee_period;
      if (data.pos_system !== undefined) updateData.pos_system = data.pos_system;
      // installation_type is handled above (line 109-112)
      if (data.excluded_from_upcoming_launches !== undefined) updateData.excluded_from_upcoming_launches = data.excluded_from_upcoming_launches;
      if (data.campaign_start !== undefined) updateData.campaign_start = data.campaign_start || null;
      if (data.campaign_end !== undefined) updateData.campaign_end = data.campaign_end || null;
      if (data.baseline !== undefined) {
        const trimmedBaseline = data.baseline?.trim();
        updateData.baseline = trimmedBaseline ? trimmedBaseline : null;
      }
      if (data.case_study_url !== undefined) {
        const trimmedCaseStudyUrl = data.case_study_url?.trim();
        updateData.case_study_url = trimmedCaseStudyUrl ? trimmedCaseStudyUrl : null;
      }
      if (data.campaign_status !== undefined) updateData.campaign_status = data.campaign_status;
      if (data.lifecycle_status !== undefined) updateData.lifecycle_status = data.lifecycle_status;
      if (data.archive_reason_code !== undefined) updateData.archive_reason_code = data.archive_reason_code;
      if (data.archive_reason_detail !== undefined) updateData.archive_reason_detail = data.archive_reason_detail;
      if (data.contract_end_date !== undefined) updateData.contract_end_date = data.contract_end_date;
      if (data.contract_end_reason !== undefined) updateData.contract_end_reason = data.contract_end_reason;
      if (data.ended_by !== undefined) updateData.ended_by = data.ended_by;
      if (data.archived_at !== undefined) {
        updateData.archived_at = data.archived_at;
        updateData.archived_by = data.archived_at ? userId : null;
      }
      if (data.archived_by !== undefined) updateData.archived_by = data.archived_by;

      const { data: updatedLocation, error } = await supabase
        .from('locations')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating location:', error);

        if (error.message?.includes('store_name') && updateData.store_name !== undefined) {
          throw new Error(STORE_NAME_MIGRATION_ERROR);
        }

        if (
          (error.message?.includes('campaign_start') && updateData.campaign_start !== undefined) ||
          (error.message?.includes('campaign_end') && updateData.campaign_end !== undefined) ||
          (error.message?.includes('baseline') && updateData.baseline !== undefined) ||
          (error.message?.includes('case_study_url') && updateData.case_study_url !== undefined) ||
          (error.message?.includes('campaign_status') && updateData.campaign_status !== undefined)
        ) {
          throw new Error(CUSTOMER_OPS_CAMPAIGN_MIGRATION_ERROR);
        }

        if (
          (error.message?.includes('lifecycle_status') && updateData.lifecycle_status !== undefined) ||
          (error.message?.includes('archive_reason_code') && updateData.archive_reason_code !== undefined) ||
          (error.message?.includes('contract_end_reason') && updateData.contract_end_reason !== undefined)
        ) {
          throw new Error(LOCATION_LIFECYCLE_MIGRATION_ERROR);
        }
        
        // If optional columns are missing in the live schema, retry without them.
        if (
          (error.message?.includes('installation_type') && updateData.installation_type !== undefined) ||
          (error.message?.includes('contract_file_path') && updateData.contract_file_path !== undefined)
        ) {
          const updateDataWithoutInstallationType = { ...updateData };
          delete updateDataWithoutInstallationType.installation_type;
          delete updateDataWithoutInstallationType.contract_file_path;
          
          const { data: retryUpdatedLocation, error: retryError } = await supabase
            .from('locations')
            .update(updateDataWithoutInstallationType)
            .eq('id', id)
            .select()
            .single();
          
          if (retryError) {
            throw new Error(`Failed to update location: ${retryError.message}`);
          }
          
          return retryUpdatedLocation as Location;
        }
        
        throw new Error(`Failed to update location: ${error.message}`);
      }

      if (!updatedLocation) {
        throw new Error('Location not found');
      }

      const shouldSyncPrimaryLaunch =
        data.installation_status !== undefined ||
        data.installation_date !== undefined ||
        data.launch_date !== undefined ||
        data.installation_type !== undefined ||
        data.excluded_from_upcoming_launches !== undefined;

      if (shouldSyncPrimaryLaunch) {
        const primaryLaunch = await locationLaunchesService.ensurePrimaryForLocation({
          locationId: updatedLocation.id,
          status: updatedLocation.installation_status,
          installationDate: updatedLocation.installation_date,
          launchDate: updatedLocation.launch_date,
          installationType: updatedLocation.installation_type ?? null,
          excludedFromUpcoming: updatedLocation.excluded_from_upcoming_launches,
          notes: 'Primary launch synced from location',
        });

        await locationLaunchesService.update(primaryLaunch.id, {
          status: updatedLocation.installation_status,
          installation_date: updatedLocation.installation_date,
          launch_date: updatedLocation.launch_date,
          installation_type: updatedLocation.installation_type ?? null,
          excluded_from_upcoming_launches: updatedLocation.excluded_from_upcoming_launches,
        });
      }

      return updatedLocation as Location;
    } catch (error) {
      console.error('Error updating location:', error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      const { error } = await supabase.from('locations').delete().eq('id', id);

      if (error) {
        console.error('Error deleting location:', error);
        throw new Error(`Failed to delete location: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting location:', error);
      throw error;
    }
  },
};
